/**
 * CD Week Plan (Shruti, 29 Oct): one short list a CD can actually do in a
 * week, then a bonus list for when it is all done.
 *
 *   This week  first the PRIORITY jobs, every one that holds (Shruti, 29 Oct:
 *              many incidents, checklists missed, teachers late or updates
 *              missed again and again must be looked into first); then 3
 *              teachers — the lowest overall scores (a CD can work with at
 *              most 3 a week): observe her class, fix her one weakest area,
 *              her life catch-up in the same sitting.
 *   Bonus      the next BONUS_JOBS of BONUS_ORDER that hold.
 *   Doing well teachers to appreciate (Shruti, 29 Oct): improved week on
 *              week (one parameter up RISE or more last week, or overall up
 *              MOVE), or consistent on any parameter (CONSISTENT or more in
 *              every scored week of the 4, at least STEADY_WEEKS of them).
 *
 * Every item is { key, kind: teacher | job | praise, title, pills, fact,
 * steps }; `key` is what a log is saved against. No lists of people
 * (Shruti, 29 Oct: too much); the tiles' own lists have them.
 *
 * Scores and wording come from the Actions tab's own rules (raiseActions),
 * over the same 4 full weeks, so the two never disagree. Rules in plain
 * words: oh-api/apps/oh_operations/CD_WEEK_ACTIONS_SPEC.md.
 */
import {
  METRIC_LABEL,
  MOVE,
  WEAK,
  raiseActions
} from 'app/pages/Audits/TeacherScorecard/actionRules'
import {
  SECTIONS,
  addDays,
  isLeader,
  isPreschool,
  moments,
  observedBetween,
  punctuality as punctualityOf,
  scoresForDates,
  weekDays,
  weeklyWork
} from 'app/pages/Audits/TeacherScorecard/scoring'

export const TEACHERS_A_WEEK = 3
export const BONUS_JOBS = 3
// Doing well: consistent = this or more on one parameter in every scored
// week (at least STEADY_WEEKS of the 4; a week off has no score); improved
// = last week on the week before, one parameter up RISE or more, or overall
// up MOVE (0.5, as the Actions tab judges). At most PRAISE_A_WEEK teachers,
// CONSISTENT_A_WEEK of them for consistency alone.
export const CONSISTENT = 4
export const STEADY_WEEKS = 3
export const RISE = 1
export const PRAISE_A_WEEK = 5
export const CONSISTENT_A_WEEK = 3
// Weekly Submissions and Lesson Planner are class-level (a hand-in by any of
// a class's teachers counts for all), so consistency on what a teacher does
// herself is named first.
const OWN_WORK = ['pun', 'mom', 'env', 'fac']
// Punctuality: half the team or more under WEAK = check the data first.
export const TEAM_PUNCTUALITY_SHARE = 0.5
// Always in the list when they hold, in this order, above the teachers.
// Punctuality and daily updates come from the scorecard (any teacher under
// WEAK for the last 4 weeks); safety and the checklist from the server.
export const PRIORITY_JOBS = [
  'incidents',
  'checklist',
  'punctuality',
  'daily_updates'
]
// HQ issues are HQ's to pick up, so they are not on the plan.
export const BONUS_ORDER = [
  'onboarding',
  'catch_ups',
  'ptm',
  'newsletter',
  'weekly_obs'
]
// A teacher's one thing to fix is never one of these: the priority jobs
// above already cover them (it still shows under Also low).
const COVERED = ['pun', 'mom']

const norm = (s) => (s || '').toLowerCase().replace(/[^a-z]/g, '')
const sameName = (a, b) => {
  const [x, y] = [norm(a), norm(b)]
  return x && y && (x === y || x.startsWith(y) || y.startsWith(x))
}

// The plan is made on the Saturday before its week (Shruti, 29 Oct), from
// the 4 weeks up to that Friday: the week running counts, Monday to Friday.
// (The scorecard itself leaves a running week's hand-ins out until it ends.)
export const scoreWeeks = (weeks, monday) => {
  const cutoff = addDays(monday, -3)
  return {
    now: weeks.filter((m) => m < monday).slice(-4),
    cutoff,
    days: (m) => weekDays(m).filter((d) => d <= cutoff)
  }
}
export const scoreDates = (weeks, monday) => {
  const span = scoreWeeks(weeks, monday)
  return span.now.flatMap(span.days)
}

const f1 = (v) => (v === null || v === undefined ? '–' : v.toFixed(1))
// "Kirti, Annu and Shyla"
const namesOf = (names) =>
  names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    : names[0] || ''
const sectionLabel = (key) => SECTIONS.find((x) => x.key === key).label
const firstName = (t) => (t.name || '').split(' ')[0]

// Plain words (Shruti, 29 Oct: "what the numbers show" was not clear): a
// score is said as the counts behind it — days on time, updates sent on
// time, plans handed in — never as "2.5 out of 5".
const COUNTS = {
  pun: (t, dates) => {
    const p = punctualityOf(t, dates)
    return p.worked ? { got: p.on, of: p.worked } : null
  },
  mom: (t, dates) => {
    const m = moments(t, dates)
    return m.worked
      ? { got: m.withM, of: m.worked, preschool: !!m.preschool }
      : null
  },
  sub: (t, dates) => {
    const w = weeklyWork(t, dates, 'evals')
    return w.due ? { got: w.done, of: w.due } : null
  },
  plan: (t, dates) => {
    const w = weeklyWork(t, dates, 'plans')
    return w.due ? { got: w.done, of: w.due } : null
  },
  env: (t, dates) => {
    const o = observedBetween(t, dates[0], dates[dates.length - 1], 'env')
    return o.n ? { got: o.yes, of: o.n } : null
  },
  fac: (t, dates) => {
    const o = observedBetween(t, dates[0], dates[dates.length - 1], 'fac')
    return o.n ? { got: o.yes, of: o.n } : null
  }
}
const countsOf = (t, key, dates) =>
  COUNTS[key] && dates.length ? COUNTS[key](t, dates) : null

// "was on time on 10 of 17 working days"
const did = (key, c) => {
  const n = `${c.got} of ${c.of}`
  if (key === 'pun') return `was on time on ${n} working days`
  if (key === 'mom')
    return c.preschool
      ? `sent ${n} daily updates on time`
      : `gave enough moments to ${n} children`
  if (key === 'sub') return `handed in ${n} weekly observations`
  if (key === 'plan') return `handed in ${n} lesson plans`
  return `met ${n} ${sectionLabel(key)} points in classroom observations`
}

// "arriving on time" — what to thank her for keeping up
const HABIT = {
  pun: 'arriving on time',
  mom: 'sending her daily updates on time',
  sub: 'handing in her weekly observations',
  plan: 'handing in her lesson plans',
  env: 'how she sets up her classroom',
  fac: 'how she runs her class'
}

// "was on time on at least 80% of her working days"
const didSteadily = (key, preschool) => {
  const pct = `${CONSISTENT * 20}%`
  if (key === 'pun') return `was on time on at least ${pct} of her working days`
  if (key === 'mom')
    return preschool
      ? `sent at least ${pct} of her daily updates on time`
      : `gave enough moments to at least ${pct} of her children`
  if (key === 'sub')
    return `handed in at least ${pct} of her weekly observations`
  if (key === 'plan') return `handed in at least ${pct} of her lesson plans`
  return `met at least ${pct} of the ${sectionLabel(
    key
  )} points in her classroom observations`
}

export const weekPlan = ({ teachers, weeks, monday, centreActions = [] }) => {
  const span = scoreWeeks(weeks, monday)
  const dates = span.now.flatMap(span.days)
  const staff = teachers.filter((t) => isPreschool(t) && !isLeader(t))
  const { list } = raiseActions(staff, dates)
  const scores = new Map(staff.map((t) => [t.no, scoresForDates(t, dates)]))
  const byNo = new Map(staff.map((t) => [t.no, t]))

  // Punctuality: every teacher under WEAK for the last 4 weeks, one job.
  const punLow = list.filter((a) => a.kind === 'pun')
  const punScored = staff.filter((t) => scores.get(t.no).pun !== null)
  const most = punLow.length >= punScored.length * TEAM_PUNCTUALITY_SHARE
  const punctuality = punLow.length
    ? {
        key: 'punctuality',
        title: 'Punctuality',
        // Names in the sentence, no list (Shruti, 29 Oct).
        why: `${namesOf(
          punLow
            .sort((a, b) => a.value - b.value)
            .map((a) => firstName(byNo.get(a.no)))
        )} came in late on many days in the last 4 weeks (${
          punLow.length
        } of the ${punScored.length} teachers).`,
        todo: most
          ? 'Check the attendance data is right first. If it is, raise arrival time at the team meeting and talk to each of them.'
          : 'Talk to each of them about arrival time; check again next week.'
      }
    : null

  // Daily updates: every teacher whose Moments (preschool: Daily Updates
  // sent on time) is under WEAK for the last 4 weeks, one job.
  const momLow = list.filter((a) => a.kind === 'mom')
  const dailyUpdates = momLow.length
    ? {
        key: 'daily_updates',
        title: 'Daily updates',
        why: `${namesOf(
          momLow
            .sort((a, b) => a.value - b.value)
            .map((a) => firstName(byNo.get(a.no)))
        )} sent many daily updates late, or not at all, in the last 4 weeks.`,
        todo: `With ${
          momLow.length > 1 ? 'each of them' : 'her'
        }, find which update slips (arrival, lunch or nap) and set a reminder for that time.`
      }
    : null

  // Teachers with a data fix (no Alfred link, no class) can't be scored yet,
  // so they are never picked.
  const unscorable = new Set(
    list.filter((a) => a.tier === 'data').map((a) => a.no)
  )

  const catchUps = centreActions.find((a) => a.key === 'catch_ups')
  const catchUpDue = (name) =>
    !!catchUps && catchUps.people.some((p) => sameName(p.name, name))

  const focus = staff
    .filter((t) => !unscorable.has(t.no) && scores.get(t.no).overall !== null)
    .sort((a, b) => scores.get(a.no).overall - scores.get(b.no).overall)
    .slice(0, TEACHERS_A_WEEK)
    .map((t) => {
      const s = scores.get(t.no)
      const mine = list
        .filter((a) => a.no === t.no && a.tier !== 'data')
        .sort((a, b) => (a.value ?? 0) - (b.value ?? 0))
      const own = mine.filter((a) => !COVERED.includes(a.kind))
      const weakest = own[0] || null
      // Low areas a priority job above already takes up.
      const covered = mine.filter((a) => COVERED.includes(a.kind))
      const shown = weakest || covered[0] || null
      const steps = []
      if (s.env === null || s.fac === null)
        steps.push(
          'Watch one of her classes and fill in a classroom observation.'
        )
      else steps.push('Watch one of her classes.')
      if (weakest) steps.push(weakest.todo)
      if (catchUpDue(t.name))
        steps.push('Do her life catch-up in the same sitting.')
      return {
        key: `teacher|${t.no}`,
        kind: 'teacher',
        title: t.name,
        pills: [
          { v: s.overall, text: `${f1(s.overall)} overall` },
          ...(shown
            ? [
                {
                  v: shown.value,
                  text: `${METRIC_LABEL[shown.metric]} ${f1(shown.value)}`
                }
              ]
            : [])
        ],
        fact: [
          `${firstName(t)}'s overall score is ${f1(
            s.overall
          )} out of 5, one of the 3 lowest at the centre.`,
          ...own.map((a, i) => {
            const c = countsOf(t, a.metric, dates)
            if (!c) return ''
            return `In the last 4 weeks she ${i ? 'also ' : ''}${did(
              a.metric,
              c
            )}.`
          }),
          ...covered.map((a) => {
            const c = countsOf(t, a.metric, dates)
            return c
              ? `She ${did(a.metric, c)} (see ${
                  a.kind === 'pun' ? 'Punctuality' : 'Daily updates'
                } above).`
              : ''
          })
        ]
          .filter(Boolean)
          .join(' '),
        steps
      }
    })

  const toItem = (a) => ({
    key: a.key,
    kind: 'job',
    title: a.title,
    pills: [],
    fact: a.why,

    steps: a.todo ? [a.todo] : []
  })
  const held = [
    ...centreActions,
    ...(punctuality ? [punctuality] : []),
    ...(dailyUpdates ? [dailyUpdates] : [])
  ]
  const inOrder = (order) =>
    held
      .filter((a) => order.includes(a.key))
      .sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key))
      .map(toItem)
  return {
    main: [...inOrder(PRIORITY_JOBS), ...focus],
    bonus: inOrder(BONUS_ORDER).slice(0, BONUS_JOBS),
    praise: doingWell(staff, scores, span, unscorable)
  }
}

// Teachers to appreciate (Shruti, 29 Oct: both week-on-week improvement and
// consistency on any parameter). One card per teacher with every reason
// that holds. Order: both reasons, then the biggest rise, then consistency
// (her own work before class-level hand-ins). Anyone can be here, the
// week's 3 teachers too.
const riseOf = (prev, last) => {
  const top = SECTIONS.map(({ key }) => ({
    key,
    d: last[key] !== null && prev[key] !== null ? last[key] - prev[key] : null
  }))
    .filter((x) => x.d !== null && x.d >= RISE)
    .sort((a, b) => b.d - a.d)[0]
  if (top)
    return {
      key: top.key,
      d: top.d,
      v: last[top.key],
      from: f1(prev[top.key]),
      to: f1(last[top.key]),
      label: sectionLabel(top.key),
      text: `${sectionLabel(top.key)} up from ${f1(prev[top.key])} to ${f1(
        last[top.key]
      )} last week.`
    }
  if (
    prev.overall !== null &&
    last.overall !== null &&
    last.overall - prev.overall >= MOVE
  )
    return {
      key: null,
      d: last.overall - prev.overall,
      v: last.overall,
      from: f1(prev.overall),
      to: f1(last.overall),
      label: 'Overall',
      text: `Overall up from ${f1(prev.overall)} to ${f1(
        last.overall
      )} last week.`
    }
  return null
}

const steadyOf = (weekly) =>
  SECTIONS.map(({ key }) => ({
    key,
    vals: weekly.map((w) => w[key]).filter((v) => v !== null)
  }))
    .filter(
      (x) =>
        x.vals.length >= STEADY_WEEKS && x.vals.every((v) => v >= CONSISTENT)
    )
    .map((x) => ({
      ...x,
      label: sectionLabel(x.key),
      own: OWN_WORK.includes(x.key),
      avg: x.vals.reduce((a, b) => a + b, 0) / x.vals.length
    }))
    .sort((a, b) => b.own - a.own || b.avg - a.avg)[0] || null

// "Sneha sent 9 of 30 daily updates on time the week before, and 26 of 30
// last week."
const riseSaid = (t, name, rise, prevDays, lastDays) => {
  const a = rise.key && countsOf(t, rise.key, prevDays)
  const b = rise.key && countsOf(t, rise.key, lastDays)
  if (a && b)
    return `${name} ${did(rise.key, a)} the week before last, and ${b.got} of ${
      b.of
    } last week.`
  return `${name}'s overall score went up from ${rise.from} to ${rise.to} out of 5 last week.`
}

const doingWell = (staff, scores, span, unscorable) => {
  const weeks = span.now
  if (weeks.length < 2) return []
  const found = staff
    .filter((t) => !unscorable.has(t.no))
    .map((t) => {
      const weekly = weeks.map((m) => scoresForDates(t, span.days(m)))
      const [prev, last] = weekly.slice(-2)
      const [prevDays, lastDays] = weeks.slice(-2).map(span.days)
      const name = firstName(t)
      const rise = riseOf(prev, last)
      const steady = steadyOf(weekly)
      if (!rise && !steady) return null
      let rank = 0
      if (!(rise && steady))
        rank = rise ? 100 - rise.d : 200 + (steady.own ? 0 : 100) - steady.avg
      const steps = []
      if (rise)
        steps.push('Tell her you have noticed the change, and what made it.')
      if (steady)
        steps.push(
          `Thank her for ${
            HABIT[steady.key]
          } week after week, in front of the team if you can.`
        )
      return {
        rank,
        alone: !rise,
        item: {
          key: `praise|${t.no}`,
          kind: 'praise',
          title: t.name,
          pills: [
            ...(rise ? [{ v: rise.v, text: `Improved: ${rise.label}` }] : []),
            ...(steady
              ? [{ v: steady.avg, text: `Consistent: ${steady.label}` }]
              : [])
          ],
          fact: [
            rise ? riseSaid(t, name, rise, prevDays, lastDays) : '',
            steady
              ? `${name} ${didSteadily(
                  steady.key,
                  isPreschool(t)
                )}, every week this past month.`
              : ''
          ]
            .filter(Boolean)
            .join(' '),

          steps
        }
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.rank - b.rank)
  let alone = 0
  return found
    .filter((x) => !x.alone || (alone += 1) <= CONSISTENT_A_WEEK)
    .slice(0, PRAISE_A_WEEK)
    .map((x) => x.item)
}
